
(function () {
  'use strict';
  
  const DRAWER_SECTION = 'cart-drawer-render';
  const SECTIONS = [DRAWER_SECTION, 'header'];
 
  const SCROLL_SELECTORS = [
    '.cart-drawer__inner',
    '[data-cart-content]',
    '.cart-drawer_offer_fields',
  ];
 
  const FOCUS_SELECTORS = [
    '[data-quantity-plus]',
    '[data-quantity-minus]',
    '[data-quantity-input]',
    '[data-discount-input]',
    '[data-cart-note]',
  ];

  const upsellHtmlCache = {};

  async function postCart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.description || data.message || 'Cart request failed');
      // Shopify's own, already-localized explanation (e.g. "Only 50 items were added…").
      error.cartDescription = typeof data.description === 'string' ? data.description : null;
      error.status = response.status;
      throw error;
    }
    return data;
  }

  /** First integer in Shopify's availability message ("Only 50 items were added…"), else null. */
  function parseServerCap(description) {
    if (typeof description !== 'string') return null;
    const match = description.match(/\d+/);
    return match ? parseInt(match[0], 10) : null;
  }

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-cart-dialog]');
      this.status = this.querySelector('[data-cart-status]');
      this.errorText = this.dataset.errorText || 'Something went wrong.';
      this.busy = false;

      this.changeUrl = this.dataset.cartChangeUrl;
      this.updateUrl = this.dataset.cartUpdateUrl;
      this.clearUrl = this.dataset.cartClearUrl;
      this.updatedText = this.dataset.updatedText || '';
      this.discountError = this.dataset.discountError || this.errorText;
      this.discountApplied = this.dataset.discountApplied || '';

      this.collapseState = {};
      // variantId → sellable quantity learned from a Shopify rejection. Kept on the instance
      // because re-rendered line HTML resets data-inventory-quantity to Liquid's value.
      this.serverCaps = {};
      // variantId → true once an over-stock increase has been sent to Shopify to learn its cap.
      this.probed = {};

      this.dialog.addEventListener('hidden.bs.offcanvas', () => {
        if (this.opener && document.contains(this.opener)) this.opener.focus();
      });


      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
        if (!button) return;
        event.preventDefault();
        this.onStepperClick(button);
      });

      // Manual quantity typing.
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-quantity-input]');
        if (!input) return;
        const line = Number(input.dataset.line);
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        this.updateLine(line, quantity);
      });

      // Remove links.
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-remove]');
        if (!remove) return;
        event.preventDefault();
        this.updateLine(Number(remove.dataset.line), 0);
      });

   
      this.addEventListener('input', (event) => {
        const note = event.target.closest('[data-cart-note]');
        if (!note) return;
        clearTimeout(this.noteTimer);
        this.noteTimer = setTimeout(() => this.updateNote(note.value), 500);
      });

      
      this.addEventListener('click', (event) => {
        if (!event.target.closest('[data-remove-all]')) return;
        event.preventDefault();
        this.clearCart();
      });

      
      ['shown.bs.collapse', 'hidden.bs.collapse'].forEach((type) => {
        this.addEventListener(type, (event) => {
          const key = event.target.dataset && event.target.dataset.collapseBody;
          if (key) this.collapseState[key] = type === 'shown.bs.collapse';
        });
      });

    
      this.addEventListener('click', (event) => {
        const apply = event.target.closest('[data-discount-apply]');
        if (apply) {
          event.preventDefault();
          this.applyDiscount();
          return;
        }
        const remove = event.target.closest('[data-discount-remove]');
        if (remove) {
          event.preventDefault();
          this.removeDiscount(remove.dataset.discountCode);
        }
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        if (!event.target.closest('[data-discount-input]')) return;
        event.preventDefault();
        this.applyDiscount();
      });


      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-cart-drawer-open]');
        if (!opener) return;
        event.preventDefault();
        this.opener = opener;
        this.open();
      });
 
      document.addEventListener('cart:refresh', (event) => {
        if (event.detail && event.detail.sections) this.renderSections(event.detail.sections);
        if (!event.detail || event.detail.open !== false) this.open();
      });

      this.watchDiscountButton();
    }

    disconnectedCallback() {
      if (this.discountObserver) this.discountObserver.disconnect();
      if (window.bootstrap && bootstrap.Offcanvas && this.dialog) {
        const instance = bootstrap.Offcanvas.getInstance(this.dialog);
        if (instance) instance.dispose();
      }
      document.querySelectorAll('.offcanvas-backdrop').forEach((backdrop) => backdrop.remove());
      document.body.classList.remove('offcanvas-backdrop', 'modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    }

    getOffcanvas() {
      if (window.bootstrap && bootstrap.Offcanvas && this.dialog) {
        return bootstrap.Offcanvas.getOrCreateInstance(this.dialog);
      }
      return null;
    }

    get isOpen() {
      return this.dialog.classList.contains('show');
    }

    open() {
      if (this.isOpen) return;
      const offcanvas = this.getOffcanvas();
      // Bootstrap manages the backdrop, focus trap and scroll lock.
      if (offcanvas) offcanvas.show();
    }

    close() {
      if (!this.isOpen) return;
      const offcanvas = this.getOffcanvas(); 
      if (offcanvas) offcanvas.hide();
    }

    onStepperClick(button) {
      const stepper = button.closest('[data-line]');
      const input = stepper.querySelector('[data-quantity-input]');
      const current = parseInt(input.value, 10) || 0;
      const next = button.hasAttribute('data-quantity-plus') ? current + 1 : current - 1;
      const quantity = Math.max(0, next);
      input.value = quantity;
      this.updateLine(Number(stepper.dataset.line), quantity);
    }

    setBusy(state) {
      this.busy = state;
      this.dialog.setAttribute('aria-busy', String(state));
      this.querySelectorAll('button[name="checkout"]').forEach((btn) => {
        btn.disabled = state;
      });
    }

    setLineBusy(line, state) {
      if (state) {
        const stepper = this.querySelector(`quantity-stepper[data-line="${line}"]`);
        const item = stepper && stepper.closest('[data-cart-item]');
        if (!item) return;
        this._busyLine = item;
        item.classList.add('cart-item--loading');
        item.setAttribute('aria-busy', 'true');
      } else if (this._busyLine) {
        this._busyLine.classList.remove('cart-item--loading');
        this._busyLine.removeAttribute('aria-busy');
        this._busyLine = null;
      }
    }

    async updateLine(line, quantity, retried) {
      if (this.busy || !line) return;
      let retry = false;
      this.clearLineErrors();
      const check = this.checkLineInventory(line, quantity);
      // First over-stock increase for a variant: send it to Shopify once so its real cap (e.g.
      // the store's add-to-cart limit) is learned instead of trusting Liquid's inventory_quantity.
      const probe = !!check.probe && !retried;
      if (probe) this.probed[check.variantId] = true;
      if (!probe && check.status === 'blocked') {
        const input = this.querySelector(`[data-quantity-input][data-line="${line}"]`);
        if (input) input.value = check.cap;
        this.showLineError(line, check.message);
        return;
      }
      if (!probe && check.status === 'clamped') quantity = check.cap;

      this.setLineBusy(line, true);
      this.setBusy(true);
      try {
        const body = { line, sections: SECTIONS, sections_url: window.location.pathname };
        let data = await postCart(`${this.changeUrl}.js`, { ...body, quantity });
        let clamped = check.status === 'clamped';
        if (probe && quantity > check.limit) {
          // Shopify accepted more than Liquid stock (no cart limit enforced): fall back to stock.
          data = await postCart(`${this.changeUrl}.js`, { ...body, quantity: check.limit });
          clamped = true;
        }
        this.renderSections(data.sections);
        if (clamped) {
          this.showLineError(line, check.message, true);
        } else {
          this.announce(this.querySelector('[data-cart-count-text]')?.textContent.trim());
        }
      } catch (error) {
        const input = this.querySelector(`[data-quantity-input][data-line="${line}"]`);
        const previous = input ? parseInt(input.defaultValue, 10) || 0 : 0;
        if (input) input.value = input.defaultValue;

        let message = error.cartDescription || this.errorText;
        const serverCap = parseServerCap(error.cartDescription);
        if (serverCap !== null) {
          const stepper = this.querySelector(`quantity-stepper[data-line="${line}"]`);
          const variantId = stepper && stepper.dataset.variantId;
          const learned = !!variantId && this.serverCaps[variantId] !== serverCap;
          if (variantId) this.serverCaps[variantId] = serverCap;
          message =
            serverCap > 0
              ? (this.dataset.textQuantityError || '').replace('[qty]', serverCap) || message
              : this.dataset.textSoldOut || message;
          // A decrease that Shopify still rejected: re-run once so the pre-check clamps to the cap.
          retry = learned && !retried && quantity < previous;
        }
        if (!retry) this.showLineError(line, message);
        console.error('[cart-drawer]', error);
      } finally {
        this.setBusy(false);
        this.setLineBusy(line, false);
        if (retry) this.updateLine(line, quantity, true);
      }
    }

    
    checkLineInventory(line, quantity) {
      const stepper = this.querySelector(`quantity-stepper[data-line="${line}"]`);
      if (!stepper) return { status: 'ok' };

      let available = parseInt(stepper.dataset.inventoryQuantity, 10);
      if (stepper.dataset.inventoryTracked !== 'true') available = NaN;
      // A cap learned from Shopify's rejection is authoritative over Liquid's inventory_quantity.
      const serverCap = this.serverCaps[stepper.dataset.variantId];
      if (typeof serverCap === 'number') {
        available = Number.isNaN(available) ? serverCap : Math.min(available, serverCap);
      }
      if (Number.isNaN(available)) return { status: 'ok' };

      const input = stepper.querySelector('[data-quantity-input]');
      const previous = input ? parseInt(input.defaultValue, 10) || 0 : 0;

      let others = 0;
      this.querySelectorAll(
        `quantity-stepper[data-variant-id="${stepper.dataset.variantId}"]`
      ).forEach((sibling) => {
        if (sibling === stepper) return;
        const siblingInput = sibling.querySelector('[data-quantity-input]');
        others += (siblingInput && parseInt(siblingInput.defaultValue, 10)) || 0;
      });
      const cap = Math.max(0, available - others);
      if (quantity <= cap) return { status: 'ok' };

      const message =
        available <= 0
          ? this.dataset.textSoldOut || this.errorText
          : (this.dataset.textQuantityError || '').replace('[qty]', available) || this.errorText;

      const variantId = stepper.dataset.variantId;
      const probe = typeof serverCap !== 'number' && !this.probed[variantId] && quantity > previous;
      const result = { cap, limit: cap, message, variantId, probe };
      // Decreasing but still above the sellable amount: drop straight to the cap.
      if (quantity < previous) return { ...result, status: 'clamped' };
      if (cap <= previous) return { ...result, status: 'blocked', cap: previous };
      return { ...result, status: 'clamped' };
    }

    showLineError(line, message, isWarning) {
      if (!message) return;
      const stepper = this.querySelector(`quantity-stepper[data-line="${line}"]`);
      const item = stepper && stepper.closest('[data-cart-item]');
      const errorEl = item && item.querySelector('[data-line-error]');
      if (errorEl) {
        errorEl.classList.toggle('cart-item__error--warning', !!isWarning);
        errorEl.hidden = false;
        const text = errorEl.querySelector('[data-line-error-message]');
        (text || errorEl).textContent = message;
      }
      this.announce(message);
    }

    clearLineErrors() {
      this.querySelectorAll('[data-line-error]').forEach((errorEl) => {
        errorEl.hidden = true;
        errorEl.classList.remove('cart-item__error--warning');
        const text = errorEl.querySelector('[data-line-error-message]');
        (text || errorEl).textContent = '';
      });
    }

    async updateNote(note) {
      try {
        await postCart(`${this.updateUrl}.js`, { note });
      } catch (error) {
        console.error('[cart-drawer]', error);
      }
    }

    async clearCart() {
      if (this.busy) return;
      this.setBusy(true);
      try {
        const data = await postCart(`${this.clearUrl}.js`, {
          sections: SECTIONS,
          sections_url: window.location.pathname,
        });
        this.renderSections(data.sections);
        this.announce(this.updatedText);
      } catch (error) {
        this.announce(this.errorText);
        console.error('[cart-drawer]', error);
      } finally {
        this.setBusy(false);
      }
    }

    /* ---------- Collapsible summary rows ---------- */

    restoreCollapseState() {
      Object.keys(this.collapseState).forEach((key) => {
        const body = this.querySelector(`[data-collapse-body="${key}"]`);
        if (!body) return;
        const open = this.collapseState[key];
        body.classList.toggle('show', open);
        const toggle = this.querySelector(`[aria-controls="${CSS.escape(body.id)}"]`);
        if (toggle) {
          toggle.classList.toggle('collapsed', !open);
          toggle.setAttribute('aria-expanded', String(open));
        }
      });
    }

    /* ---------- Scroll and focus preservation across re-render ---------- */

    captureScroll() {
      const positions = {};
      SCROLL_SELECTORS.forEach((selector) => {
        const el = this.querySelector(selector);
        if (el && el.scrollTop) positions[selector] = el.scrollTop;
      });
      return positions;
    }

    restoreScroll(positions) {
      if (!positions) return;
      Object.keys(positions).forEach((selector) => {
        const el = this.querySelector(selector);
        if (!el) return; 
        el.style.scrollBehavior = 'auto';
        el.scrollTop = positions[selector];
        el.style.removeProperty('scroll-behavior');
      });
    }

    /** Describe the focused control as a selector, since the node itself is about to be discarded. */
    captureFocus() {
      const active = document.activeElement;
      if (!active || !this.contains(active)) return null;

      const control = FOCUS_SELECTORS.find((selector) => active.matches(selector));
      if (!control) return null;
 
      const item = active.closest('[data-key]');
      if (!item || !item.dataset.key) return control;
      return `[data-key="${CSS.escape(item.dataset.key)}"] ${control}`;
    }

    restoreFocus(selector) {
      if (!selector) return;
      const target = this.querySelector(selector); 
      if (target) target.focus({ preventScroll: true });
    }

    /* ---------- Discount button width ---------- */

    /** Reserve trailing padding on the discount input equal to the Apply button width. */
    watchDiscountButton() {
      const input = this.querySelector('[data-discount-input]');
      const button = this.querySelector('[data-discount-apply]');
      if (this.discountObserver) this.discountObserver.disconnect();
      if (!input || !button || typeof ResizeObserver === 'undefined') return;

      this.discountObserver = new ResizeObserver(() => {
        const width = button.offsetWidth;
        // Hidden (drawer closed / accordion collapsed) — keep the CSS fallback.
        if (!width) return;
        input.style.setProperty('--discount-btn-width', `${width + 16}px`);
      });
      this.discountObserver.observe(button);
    }

    /* ---------- Discount codes ---------- */

    getAppliedCodes() {
      return Array.from(
        this.querySelectorAll('[data-applied-discounts] [data-discount-code]')
      ).map((el) => el.dataset.discountCode);
    }

    showDiscountError(message) {
      const errorEl = this.querySelector('[data-discount-error]');
      if (errorEl) {
        errorEl.hidden = false;
        const text = errorEl.querySelector('[data-discount-error-message]');
        (text || errorEl).textContent = message;
      }
      this.announce(message);
    }

    async applyDiscount() {
      if (this.busy) return;
      const input = this.querySelector('[data-discount-input]');
      const code = input && input.value.trim();
      if (!code) return;

      const existing = this.getAppliedCodes();
      if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
        input.value = '';
        return;
      }

      await this.postDiscounts(existing.concat(code), { attempted: code, previous: existing });
    }

    async removeDiscount(code) {
      if (this.busy || !code) return;
      const remaining = this.getAppliedCodes().filter(
        (c) => c.toLowerCase() !== code.toLowerCase()
      );
      await this.postDiscounts(remaining);
    }

    async postDiscounts(codes, attempt) {
      this.setBusy(true);
      try {
        const data = await postCart(`${this.updateUrl}.js`, {
          discount: codes.join(','),
          sections: SECTIONS,
          sections_url: window.location.pathname,
        });

        let failed = false;
        if (attempt) {
          const entries = Array.isArray(data.discount_codes) ? data.discount_codes : null;
          if (entries) {
            const entry = entries.find(
              (d) => d.code && d.code.toLowerCase() === attempt.attempted.toLowerCase()
            );
            failed = !entry || entry.applicable === false;
          }
          if (!failed) {
            const html = data.sections && data.sections[DRAWER_SECTION];
            if (html && !entries) {
              failed = !new DOMParser()
                .parseFromString(html, 'text/html')
                .querySelector(`[data-discount-code="${CSS.escape(attempt.attempted)}" i]`);
            }
          }
        }

        this.renderSections(data.sections);

        if (attempt && failed) {
          this.showDiscountError(this.discountError);
          const input = this.querySelector('[data-discount-input]');
          if (input) {
            input.value = attempt.attempted;
            input.focus();
          }
          postCart(`${this.updateUrl}.js`, { discount: attempt.previous.join(',') }).catch(
            (error) => console.error('[cart-drawer]', error)
          );
        } else if (attempt) {
          this.announce(this.discountApplied);
          const input = this.querySelector('[data-discount-input]');
          if (input) input.focus();
        }
      } catch (error) {
        this.showDiscountError(this.discountError);
        console.error('[cart-drawer]', error);
      } finally {
        this.setBusy(false);
      }
    }

    /** Swap in freshly rendered HTML for the drawer body and the header count. */
    renderSections(sections) {
      if (!sections) return;

      const drawerHtml = sections[DRAWER_SECTION];
      if (drawerHtml) {
        const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
        const newContent = doc.querySelector('[data-cart-content]');
        const oldContent = this.querySelector('[data-cart-content]');
        if (newContent && oldContent) { 
          const preserve = this.isOpen;
          const positions = preserve ? this.captureScroll() : null;
          const focused = preserve ? this.captureFocus() : null;

          oldContent.replaceWith(newContent);
          this.restoreCollapseState();
          this.watchDiscountButton();

          if (preserve) {
            this.restoreScroll(positions);
            this.restoreFocus(focused); 
            requestAnimationFrame(() => this.restoreScroll(positions));
          }
        }

        const newCount = doc.querySelector('[data-cart-count-text]');
        const oldCount = this.querySelector('[data-cart-count-text]');
        if (newCount && oldCount) oldCount.textContent = newCount.textContent;
      }

      const headerHtml = sections['header'];
      if (headerHtml) {
        const doc = new DOMParser().parseFromString(headerHtml, 'text/html');
        const newCount = doc.querySelector('[data-cart-count]');
        const oldCount = document.querySelector('[data-cart-count]');
        if (oldCount) {
          if (newCount) oldCount.replaceWith(newCount);
          else oldCount.remove();
        } else if (newCount) {
          const link = document.querySelector('[data-cart-drawer-open], .header-cart');
          if (link) link.prepend(newCount);
        }
      }
    }

    announce(message) {
      if (this.status && message) this.status.textContent = message;
    }
  }
 
  const PRODUCT_FORM_SELECTOR = [
    'form.product-add-to-cart__form',
    'form.product-card-add__form',
    'form.product-card__quick-add-form',
    'form.cart-upsell-card__form',
    'form.quiz-card__form',
    'form.before-after-product__form',
  ].join(', ');

  class ProductForm extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('submit', (event) => {
        const form = event.target;
        if (!form.matches || !form.matches(PRODUCT_FORM_SELECTOR)) return;
        this.form = form;
        this.submitButton =
          (event.submitter && event.submitter.name === 'add' && event.submitter) ||
          form.querySelector('button[name="add"]');
        this.onSubmit(event);
      });
    }

    async onSubmit(event) { 
      if (event.submitter && event.submitter.name !== 'add') return;

      event.preventDefault(); 
      const drawer = document.querySelector('cart-drawer');

      this.clearError();
      this.setLoading(true);
      try {
        const formData = new FormData(this.form);
        if (drawer) {
          formData.append('sections', SECTIONS.join(','));
          formData.append('sections_url', window.location.pathname);
        }
 
        const requested = parseInt(formData.get('quantity'), 10) || 1;
        const check = await this.checkInventory(requested);
        if (check.status === 'soldout' || check.status === 'max') { 
          if (this.showError(this.inventoryMessage(check))) return;
        }
        let clampedTo = null;
        if (check.status === 'partial') {
          clampedTo = check.remaining;
          formData.set('quantity', String(clampedTo));
        }

        const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        }); 
        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (!this.showError(this.resolveCartError(response.status, data))) {
            window.location.href = this.dataset.cartUrl || '/cart';
          }
          return;
        }
 
        let added = clampedTo;
        if (added === null) {
          const line = Array.isArray(data.items) ? data.items[0] : data;
          if (line && typeof line.quantity === 'number' && line.quantity < requested) {
            added = line.quantity;
          }
        }
        let warned = false;
        if (added !== null) {
          const errorEl = this.getErrorElement();
          const template = errorEl && errorEl.dataset.textQuantityLow;
          if (template) warned = this.showError(template.replace('[qty]', added), true);
        }

        this.announceAdded();

        if (drawer) {
          drawer.opener = this.submitButton;
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { sections: data.sections } }));
        } else if (!warned) {
          window.location.href = this.dataset.cartUrl || '/cart';
        }
      } catch (error) {
        console.error('[product-form]', error);
        const errorEl = this.getErrorElement(); 
        if (!this.showError(errorEl && errorEl.dataset.textError)) {
          window.location.href = this.dataset.cartUrl || '/cart';
        }
      } finally {
        this.setLoading(false);
      }
    }
 
    async checkInventory(requested) {
      const input = this.form && this.form.querySelector('[data-variant-id-input]');
      if (!input || input.dataset.inventoryTracked !== 'true') return { status: 'ok' };
      const available = parseInt(input.dataset.inventoryQuantity, 10);
      if (Number.isNaN(available)) return { status: 'ok' };
      if (available <= 0) return { status: 'soldout' };

      let inCart = 0;
      try {
        const response = await fetch(`${this.dataset.cartUrl || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        });
        const cart = await response.json();
        const variantId = parseInt(input.value, 10);
        inCart = cart.items
          .filter((item) => item.variant_id === variantId)
          .reduce((sum, item) => sum + item.quantity, 0);
      } catch (error) {
        return { status: 'ok' };
      }

      const remaining = available - inCart;
      if (remaining <= 0) return { status: 'max', available };
      if (requested > remaining) return { status: 'partial', remaining };
      return { status: 'ok' };
    }
 
    inventoryMessage(check) {
      const errorEl = this.getErrorElement();
      if (!errorEl) return null;
      if (check.status === 'soldout') return errorEl.dataset.textSoldOut;
      if (check.status === 'max') {
        return (errorEl.dataset.textQuantityError || '').replace('[qty]', check.available) || null;
      }
      return null;
    }

    getErrorElement() {
      if (this.form) {
        const scoped = this.form.querySelector('[data-buy-buttons-error]');
        if (scoped) return scoped;
      }
      return this.querySelector('[data-buy-buttons-error]');
    }

    /*
     * Map a failed /cart/add response to a merchant-translated message. 
     */
    resolveCartError(status, data) {
      const errorEl = this.getErrorElement();
      const strings = errorEl ? errorEl.dataset : {};
      const description =
        data && typeof data.description === 'string' && data.description
          ? data.description
          : null;
      if (status === 404) return strings.textNotFound || strings.textError;
      if (status === 429) return strings.textRateLimit || strings.textError;

      // Availability rejection: reuse the theme's wording and remember the sellable quantity so
      // the next submit is caught client-side by checkInventory.
      const serverCap = parseServerCap(description);
      if (serverCap !== null) {
        const input = this.form && this.form.querySelector('[data-variant-id-input]');
        if (input) {
          input.dataset.inventoryQuantity = String(serverCap);
          input.dataset.inventoryTracked = 'true';
          // Remembered per variant so main-product.js can re-apply it after a variant switch.
          this.serverCaps = this.serverCaps || {};
          if (input.value) this.serverCaps[input.value] = serverCap;
        }
        // Sync the stepper so +/typing warns at the real cap before the next Add click.
        const qty = this.form && this.form.elements.namedItem('quantity');
        if (qty instanceof HTMLInputElement && serverCap > 0) {
          qty.max = String(serverCap);
          if ((parseInt(qty.value, 10) || 1) > serverCap) qty.value = String(serverCap);
        }
        if (serverCap <= 0) return strings.textSoldOut || description;
        return (strings.textQuantityError || '').replace('[qty]', serverCap) || description;
      }
      return description || strings.textError;
    }
 
    showError(message, isWarning) {
      const errorEl = this.getErrorElement();
      if (!errorEl || !message) return false;
      errorEl.classList.toggle('product-add-to-cart__error--warning', !!isWarning);
      errorEl.hidden = false;
      const text = errorEl.querySelector('[data-buy-buttons-error-message]');
      (text || errorEl).textContent = message;
      this.scrollErrorIntoView(errorEl);
      return true;
    }

    scrollErrorIntoView(errorEl) {
      const rect = errorEl.getBoundingClientRect();
      const viewHeight = window.innerHeight || document.documentElement.clientHeight;
      if (rect.top >= 0 && rect.bottom <= viewHeight) return;
      errorEl.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
        block: 'center',
      });
    }

    getStatusElement() {
      return (this.form && this.form.querySelector('[data-add-to-cart-status]')) || null;
    }

    announce(message) {
      const statusEl = this.getStatusElement();
      if (statusEl && message) statusEl.textContent = message;
    }

    announceAdded() {
      const statusEl = this.getStatusElement();
      if (statusEl) this.announce(statusEl.dataset.textAdded);
    }

    clearError() {
      const errorEl = this.getErrorElement();
      if (!errorEl) return;
      errorEl.hidden = true;
      errorEl.classList.remove('product-add-to-cart__error--warning');
      const text = errorEl.querySelector('[data-buy-buttons-error-message]');
      (text || errorEl).textContent = '';
    }

    setLoading(state) {
      if (!this.submitButton) return;
      if (state) this.submitButton.setAttribute('aria-busy', 'true');
      else this.submitButton.removeAttribute('aria-busy');
      this.submitButton.disabled = state;
    }
  }
 
  class QuantityStepper extends HTMLElement {}
 
  class CartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.state === 'loaded') return;
 
      /* Server-rendered content is final unless it is only the fallback collection. */
      var existing = this.querySelector('[data-cu-content]');
      if (!this.dataset.url || (existing && !existing.hasAttribute('data-cu-fallback'))) {
        this.dataset.state = 'loaded';
        this.initCarousel();
        return;
      }
      if (upsellHtmlCache[this.dataset.url]) {
        this.dataset.state = 'loaded';
        if (upsellHtmlCache[this.dataset.url] === 'empty') {
          if (existing) this.initCarousel();
          this.handleEmpty();
        } else {
          this.applyContent(upsellHtmlCache[this.dataset.url]);
        }
        return;
      }
      /* Paint the fallback now; the fetch may still upgrade it. */
      if (existing) this.initCarousel();

      var self = this;

      if (window.Shopify && window.Shopify.designMode) {
        this.loadRecommendations();
        return;
      }

      /*
       * Inside the drawer the observer never sees us: the off-canvas is parked outside
       * the viewport, and once open the upsell can sit below the fold of the scrollable
       * body, which clips the intersection. Load when the drawer opens instead.
       */
      var dialog = this.closest('.offcanvas');
      if (dialog) {
        this._dialog = dialog;
        this._onDrawerShown = function () {
          self.loadRecommendations();
        };
        dialog.addEventListener('shown.bs.offcanvas', this._onDrawerShown);
        // Re-rendered while the drawer was already open.
        if (dialog.classList.contains('show')) this.loadRecommendations();
        return;
      }

      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver(
          function (entries) {
            if (!entries[0].isIntersecting) return;
            self._observer.disconnect();
            self._observer = null;
            self.loadRecommendations();
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        this._observer.observe(this);
      } else {
        this.loadRecommendations();
      }
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._dialog && this._onDrawerShown) {
        this._dialog.removeEventListener('shown.bs.offcanvas', this._onDrawerShown);
        this._dialog = null;
        this._onDrawerShown = null;
      }
      if (this._controller) this._controller.abort();
      this.destroyCarousel();
    }

    loadRecommendations() {
      if (this.dataset.state === 'loading' || this.dataset.state === 'loaded') return;
      this.dataset.state = 'loading';

      if (this._controller) this._controller.abort();
      this._controller = new AbortController();

      var self = this;
      fetch(this.dataset.url, { signal: this._controller.signal })
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector(
            'cart-upsell[data-section-id="' + self.dataset.sectionId + '"]'
          );
          var content = fresh && fresh.querySelector('[data-cu-content]');
          self.dataset.state = 'loaded';
          if (content) {
            upsellHtmlCache[self.dataset.url] = content.outerHTML;
            self.applyContent(content.outerHTML);
          } else {
            upsellHtmlCache[self.dataset.url] = 'empty';
            self.handleEmpty();
          }
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          self.dataset.state = 'loaded';
          self.handleEmpty();
        });
    }

    /** Swap in new markup, discarding any carousel already running on the old markup. */
    applyContent(html) {
      this.destroyCarousel();
      this.innerHTML = html;
      this.hidden = false;
      this.initCarousel();
    }

    initCarousel(attempts) {
      var el = this.querySelector('[data-cu-swiper]');
      if (!el || el.swiperInstance) return;
      if (!window.Swiper) {
        attempts = attempts || 0;
        if (attempts >= 40 || !this.isConnected) return;
        var self = this;
        setTimeout(function () {
          self.initCarousel(attempts + 1);
        }, 100);
        return;
      }

      var colsDesktop = parseInt(el.dataset.colsDesktop, 10) || 4;
      var colsMobile = parseInt(el.dataset.colsMobile, 10) || 2;
      var gap = parseInt(el.dataset.gap, 10) || 12;
      var navStyle = el.dataset.nav || 'navigation';

      var opts = {
        slidesPerView: colsMobile,
        spaceBetween: gap,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        a11y: { slideRole: 'listitem' },
        breakpoints: {}
      };
      opts.breakpoints[750] = { slidesPerView: colsDesktop, spaceBetween: gap };

      if (navStyle === 'navigation' || navStyle === 'both') {
        opts.navigation = {
          nextEl: el.querySelector('.swiper-button-next'),
          prevEl: el.querySelector('.swiper-button-prev')
        };
      }
      if (navStyle === 'pagination' || navStyle === 'both') {
        var pagEl = el.querySelector('.swiper-pagination');
        opts.pagination = { el: pagEl, clickable: true };

        if (pagEl && pagEl.dataset.paginationStyle === 'elegant') {
          opts.pagination.renderBullet = function (index, className) {
            return (
              '<span class="' + className + ' custom-bullet">' +
              '<svg class="progress-ring" viewBox="0 0 34 34" aria-hidden="true">' +
              '<circle class="progress-ring-bg" cx="17" cy="17" r="16"></circle>' +
              '<circle class="progress-ring-fill" cx="17" cy="17" r="16"></circle>' +
              '</svg>' +
              '<span class="bullet-dot"></span>' +
              '</span>'
            );
          };
        }
      }

      el.swiperInstance = new window.Swiper(el, opts);
    }

    destroyCarousel() {
      var el = this.querySelector('[data-cu-swiper]');
      if (el && el.swiperInstance) {
        el.swiperInstance.destroy(true, true);
        el.swiperInstance = null;
      }
    } 
    handleEmpty() {
      /* Keep whatever the server rendered — the fallback collection. */
      if (this.querySelector('[data-cu-content]')) return;
      if (window.Shopify && window.Shopify.designMode) return;
      this.hidden = true;
    }
  }

  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
  if (!customElements.get('quantity-stepper')) customElements.define('quantity-stepper', QuantityStepper);
  if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
})();
